/**
 * The course page, /lms/courses/:courseId (plan docs/plans/lms-plan.md
 * section 13 tab 2, 7.2, 8.3, 8.4, 18.3, 18.17, 18.22, 18.25; WP-42; R3, R9,
 * R24). LmsApp routes to this file's DEFAULT export; the course id is
 * useParams().courseId (18.3).
 *
 * The overview before the player: the title, how the course is taken, the
 * person's status chip (GET /me/status), the estimated minutes, the validity
 * and when its renewal is assigned, the current version and its publish date,
 * what changed in it, the objectives, what it does not cover, the disclaimer
 * and every source (title, publisher, URL and access date, 6.3, 18.22), and
 * ONE button to the player (18.3, /lms/courses/:courseId/learn):
 *   - "Review" when the person is current on the course with no open
 *     assignment: the player opens at its Overview and starts no attempt; if
 *     they take the quiz anyway the player offers it on LMS_ALREADY_CURRENT
 *     (18.22);
 *   - "Continue" once the bookmark (GET /me/progress/:courseId) shows
 *     progress;
 *   - "Start" otherwise.
 * An SOP course also shows its SOP number and version label (GET
 * /sops/by-course/:courseId through api/sops.ts getSopForCourse, 18.25, 18.29);
 * the "Open the SOP" button is WP-46's in the player (18.27).
 *
 * Loading and error states keep an h1 "Course" (WP-03's LmsApp.test.tsx
 * asserts it at this route, 18.22). The course read failing fails the page
 * with a Retry (G10); the status or bookmark failing keeps the overview and
 * says so. Every read re-runs on a site switch (X-Site-Id).
 */
import { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, BookOpen, Eye, Loader2, PlayCircle } from 'lucide-react';
import PageHeader from '../../../ui/PageHeader';
import { outlineBtnCls, primaryBtnCls, secondaryBtnCls } from '../../../ui/styles';
import { useLms } from '../../LmsProvider';
import { apiErrorMessage } from '../../api/client';
import { getCourse } from '../../api/courses';
import type { CourseDetail } from '../../api/courses';
import { getMyStatus } from '../../api/completions';
import type { MyCourseStatus } from '../../api/completions';
import { getMyProgress } from '../../api/progress';
import type { LmsProgress } from '../../api/progress';
import type { CourseSource } from '../../content/types';
import { lmsPageCls } from '../../components/layout/page';
import { microLabelCls, noticeCls, panelCls } from '../../components/player/styles';
import {
  categoryLabel,
  courseAction,
  formatDateOnly,
  formatInstant,
  isCurrentWithoutAssignment,
  kindLabel,
  learnHref,
  minutesLong,
  renewalText,
  validityText,
} from './catalog';
import type { CourseAction } from './catalog';
import { CourseStatusChip } from './CourseStatusChip';
import { getSopForCourse } from '../../api/sops';
import type { SopForCourse } from '../../api/sops';

/** The h1 of the loading and error states (18.22: LmsApp.test.tsx asserts it). */
export const COURSE_PAGE_FALLBACK_TITLE = 'Course';

type PageLoad =
  | { state: 'loading' }
  | { state: 'error'; message: string; notFound: boolean }
  | {
    state: 'ready';
    course: CourseDetail;
    /** The GET /me/status cell; undefined when the person has nothing on the course. */
    cell: MyCourseStatus | undefined;
    progress: LmsProgress | null;
    /** GET /me/status failed: the chip is unknown, not "Not started". */
    statusFailed: boolean;
    /** Set when GET /me/status or GET /me/progress failed (the overview still shows). */
    detailsError: string | null;
  };

type SopLoad = { state: 'none' } | { state: 'loading' } | { state: 'ready'; info: SopForCourse } | { state: 'error' };

interface HttpError {
  response?: { status?: number };
}

function httpStatus(err: unknown): number | undefined {
  return (err as HttpError | null)?.response?.status;
}

/** Only an http(s) source URL becomes a link (a content value is data, never a script URL). */
function safeHttpUrl(url: string | null | undefined): string | null {
  return typeof url === 'string' && /^https?:\/\//i.test(url.trim()) ? url.trim() : null;
}

const sectionHeadingCls = 'text-sm font-semibold text-gray-900 dark:text-gray-100';
const bodyTextCls = 'text-sm text-gray-700 dark:text-gray-300';

function BackLink() {
  return (
    <Link
      to="/lms/courses"
      className="inline-flex min-h-[44px] items-center gap-1 rounded-lg text-sm font-medium text-primary-600 transition-colors duration-150 hover:text-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-primary-400 dark:hover:text-primary-300 sm:min-h-0"
      data-testid="lms-course-back"
    >
      <ArrowLeft className="h-4 w-4" aria-hidden="true" />
      All courses
    </Link>
  );
}

export function CoursePage() {
  const { courseId } = useParams();
  const { siteId } = useLms();
  const [load, setLoad] = useState<PageLoad>({ state: 'loading' });
  const [sop, setSop] = useState<SopLoad>({ state: 'none' });
  const [tick, setTick] = useState(0);

  const reload = useCallback(() => setTick((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;
    setSop({ state: 'none' });
    if (!courseId) {
      setLoad({ state: 'error', message: 'No course was named.', notFound: true });
      return undefined;
    }
    if (!siteId) {
      setLoad({ state: 'error', message: 'Select a site to see this course.', notFound: false });
      return undefined;
    }
    setLoad({ state: 'loading' });
    Promise.allSettled([getCourse(courseId), getMyStatus(), getMyProgress(courseId)]).then(([course, status, progress]) => {
      if (cancelled) return;
      if (course.status === 'rejected') {
        const notFound = httpStatus(course.reason) === 404;
        setLoad({
          state: 'error',
          notFound,
          message: notFound
            ? 'This course was not found, or it is not available to you.'
            : apiErrorMessage(course.reason, 'The course could not be loaded.'),
        });
        return;
      }
      const detail = course.value;
      const map = status.status === 'fulfilled' && status.value && status.value.courses ? status.value.courses : {};
      const statusFailed = status.status === 'rejected';
      const progressFailed = progress.status === 'rejected';
      let detailsError: string | null = null;
      if (statusFailed && progressFailed) detailsError = 'Your status and progress on this course could not be loaded.';
      else if (statusFailed) detailsError = 'Your status on this course could not be loaded.';
      else if (progressFailed) detailsError = 'Your progress on this course could not be loaded, so the button may say Start where Continue applies.';
      setLoad({
        state: 'ready',
        course: detail,
        cell: map[detail.id] ?? map[courseId],
        progress: progress.status === 'fulfilled' ? progress.value ?? null : null,
        statusFailed,
        detailsError,
      });

      // 18.25: an SOP course names the SOP version it trains on.
      if (detail.category === 'sop') {
        setSop({ state: 'loading' });
        getSopForCourse(detail.id, detail.current_version?.id ?? null).then(
          (info) => {
            if (!cancelled) setSop({ state: 'ready', info });
          },
          (err) => {
            if (!cancelled) setSop(httpStatus(err) === 404 ? { state: 'none' } : { state: 'error' });
          }
        );
      }
    });
    return () => {
      cancelled = true;
    };
  }, [courseId, siteId, tick]);

  if (load.state === 'loading') {
    return (
      <div className={lmsPageCls} data-testid="lms-course-page">
        <BackLink />
        <PageHeader title={COURSE_PAGE_FALLBACK_TITLE} icon={BookOpen} />
        <div role="status" className="flex items-center gap-2 py-8 text-sm text-gray-600 dark:text-gray-400" data-testid="lms-course-loading">
          <Loader2 className="h-5 w-5 animate-spin text-primary-600 dark:text-primary-400" aria-hidden="true" />
          Loading the course...
        </div>
      </div>
    );
  }

  if (load.state === 'error') {
    return (
      <div className={lmsPageCls} data-testid="lms-course-page">
        <BackLink />
        <PageHeader title={COURSE_PAGE_FALLBACK_TITLE} icon={BookOpen} />
        <div className={noticeCls('red')} role="alert" data-testid="lms-course-error">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
          <div className="flex flex-1 flex-wrap items-center justify-between gap-2">
            <span>{load.message}</span>
            {!load.notFound && siteId && (
              <button type="button" className={secondaryBtnCls} onClick={reload}>
                Retry
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  const { course, cell, progress, statusFailed, detailsError } = load;
  const version = course.current_version;
  const objectives = Array.isArray(course.objectives) ? course.objectives : [];
  const sources = Array.isArray(course.sources) ? course.sources : [];
  const published = version !== null && course.is_active !== false;
  const action = courseAction(cell, progress);
  const actionButton = published ? <ActionLink courseId={course.id} action={action} /> : null;

  return (
    <div className={lmsPageCls} data-testid="lms-course-page">
      <BackLink />
      <PageHeader
        title={course.title}
        icon={BookOpen}
        subtitle={`${categoryLabel(String(course.category))} · ${kindLabel(course.kind)}`}
        actions={actionButton}
      />

      {detailsError && (
        <div className={noticeCls('amber')} role="status" data-testid="lms-course-details-error">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
          <div className="flex flex-1 flex-wrap items-center justify-between gap-2">
            <span>{detailsError}</span>
            <button type="button" className={outlineBtnCls()} onClick={reload}>
              Retry
            </button>
          </div>
        </div>
      )}

      {!published && (
        <div className={noticeCls('amber')} role="status" data-testid="lms-course-unavailable">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
          <span>
            {course.is_active === false
              ? 'This course is switched off, so learners cannot see or take it.'
              : 'This course has no published version yet, so it cannot be taken.'}
          </span>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        <aside className="space-y-4 lg:order-2" aria-label="Course facts">
          <div className={`${panelCls} space-y-4`}>
            <dl className="space-y-3">
              <Fact label="Your status" testId="lms-course-fact-status">
                {statusFailed ? (
                  <span className="text-gray-600 dark:text-gray-400">Not available</span>
                ) : (
                  <span className="flex flex-wrap items-center gap-2">
                    <CourseStatusChip cell={cell} />
                    {cell?.status === 'overdue' && formatInstant(cell.due_at) && (
                      <span className="text-xs text-gray-600 dark:text-gray-400">{`Was due ${formatInstant(cell.due_at)}`}</span>
                    )}
                    {(cell?.status === 'compliant' || cell?.status === 'expiring') && cell.assignment_id && formatInstant(cell.due_at) && (
                      <span className="text-xs text-gray-600 dark:text-gray-400">{`Renewal due ${formatInstant(cell.due_at)}`}</span>
                    )}
                  </span>
                )}
              </Fact>
              {minutesLong(version?.estimated_minutes) && (
                <Fact label="Time" testId="lms-course-minutes">
                  {minutesLong(version?.estimated_minutes)}
                </Fact>
              )}
              <Fact label="Validity" testId="lms-course-validity">
                <span className="block">{validityText(course.validity_months)}</span>
                {renewalText(course.validity_months, course.renewal_lead_days) && (
                  <span className="block text-xs text-gray-600 dark:text-gray-400">
                    {renewalText(course.validity_months, course.renewal_lead_days)}
                  </span>
                )}
              </Fact>
              {version && (
                <Fact label="Version" testId="lms-course-version">
                  {formatInstant(version.published_at)
                    ? `Version ${version.version_no}, published ${formatInstant(version.published_at)}`
                    : `Version ${version.version_no}`}
                </Fact>
              )}
              {sop.state === 'ready' && (
                <Fact label="SOP" testId="lms-course-sop">
                  <span className="block">{`SOP ${sop.info.sop.sop_number}: ${sop.info.sop.title}`}</span>
                  {sop.info.version && (
                    <span className="block text-xs text-gray-600 dark:text-gray-400">
                      {formatDateOnly(sop.info.version.effective_on)
                        ? `Version ${sop.info.version.version_label}, effective ${formatDateOnly(sop.info.version.effective_on)}`
                        : `Version ${sop.info.version.version_label}`}
                    </span>
                  )}
                </Fact>
              )}
              {sop.state === 'error' && (
                <Fact label="SOP" testId="lms-course-sop">
                  <span className="text-gray-600 dark:text-gray-400">The SOP details could not be loaded.</span>
                </Fact>
              )}
            </dl>
            {published && isCurrentWithoutAssignment(cell) && (
              <p className="border-t border-gray-200 pt-3 text-xs text-gray-600 dark:border-gray-700 dark:text-gray-400" data-testid="lms-course-review-hint">
                You are current on this course. Review opens the slides and the reading; you do not need to take the quiz again before your renewal.
              </p>
            )}
          </div>
        </aside>

        <div className="space-y-4 lg:order-1 lg:col-span-2">
          {(course.description || objectives.length > 0 || course.not_covered || course.disclaimer) && (
            <section className={`${panelCls} space-y-4`} aria-label="Overview">
              {course.description && <p className={bodyTextCls}>{course.description}</p>}
              {objectives.length > 0 && (
                <div>
                  <h2 className={sectionHeadingCls}>What you will be able to do</h2>
                  <ul className={`mt-2 list-disc space-y-1 pl-5 ${bodyTextCls}`} data-testid="lms-course-objectives">
                    {objectives.map((objective, i) => (
                      <li key={i}>{objective}</li>
                    ))}
                  </ul>
                </div>
              )}
              {course.not_covered && (
                <p className={bodyTextCls} data-testid="lms-course-not-covered">
                  <span className="font-semibold text-gray-900 dark:text-gray-100">Not covered here: </span>
                  {course.not_covered}
                </p>
              )}
              {course.disclaimer && <div className={noticeCls('amber')}>{course.disclaimer}</div>}
            </section>
          )}

          {version && version.version_no > 1 && course.change_summary && (
            <section className={`${panelCls} space-y-2`} data-testid="lms-course-change-summary">
              <h2 className={microLabelCls}>{`What changed in version ${version.version_no}`}</h2>
              <p className={`whitespace-pre-line ${bodyTextCls}`}>{course.change_summary}</p>
            </section>
          )}

          <section className={`${panelCls} space-y-2`} aria-labelledby="lms-course-sources-heading">
            <h2 id="lms-course-sources-heading" className={sectionHeadingCls}>
              {`Sources (${sources.length})`}
            </h2>
            {sources.length === 0 ? (
              <p className="text-sm text-gray-600 dark:text-gray-400">No sources are listed for this version.</p>
            ) : (
              <ol className="list-decimal space-y-2 pl-5" data-testid="lms-course-sources">
                {sources.map((source, i) => (
                  <SourceItem key={i} source={source} />
                ))}
              </ol>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

function Fact({ label, testId, children }: { label: string; testId: string; children: ReactNode }) {
  return (
    <div>
      <dt className={microLabelCls}>{label}</dt>
      <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100" data-testid={testId}>
        {children}
      </dd>
    </div>
  );
}

/** The one button to the player (18.3): Start, Continue or Review. */
function ActionLink({ courseId, action }: { courseId: string; action: CourseAction }) {
  const Icon = action.kind === 'review' ? Eye : PlayCircle;
  return (
    <Link to={learnHref(courseId)} className={`${primaryBtnCls} sm:px-4 sm:py-2`} data-testid="lms-course-action" data-action={action.kind}>
      <Icon className="h-4 w-4" aria-hidden="true" />
      {action.label}
    </Link>
  );
}

/** One source (6.3, G8): title, publisher, URL and the date it was accessed, plus its note (18.22). */
function SourceItem({ source }: { source: CourseSource }) {
  const url = safeHttpUrl(source.url);
  const accessed = formatDateOnly(source.accessed_on);
  const byline = [source.publisher, accessed ? `accessed ${accessed}` : null].filter((part): part is string => Boolean(part));
  return (
    <li className="text-sm text-gray-900 dark:text-gray-100" data-testid="lms-course-source">
      <span className="font-medium">{source.title}</span>
      {byline.length > 0 && <span className="block text-xs text-gray-600 dark:text-gray-400">{byline.join(', ')}</span>}
      {url ? (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="block break-all text-xs text-primary-600 underline underline-offset-2 hover:text-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-primary-400 dark:hover:text-primary-300"
        >
          {url}
        </a>
      ) : (
        source.url && <span className="block break-all text-xs text-gray-600 dark:text-gray-400">{source.url}</span>
      )}
      {source.note && <span className="block text-xs text-gray-600 dark:text-gray-400">{source.note}</span>}
    </li>
  );
}

export default CoursePage;
